<template>
  <div v-if="visible" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
      <!-- 对话框内容 -->
      <div class="p-6">
        <div class="flex items-start space-x-4">
          <div class="flex-shrink-0">
            <!-- 根据类型显示不同的图标 -->
            <svg v-if="type === 'delete'" class="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
            </svg>
            <svg v-else-if="type === 'audit'" class="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <svg v-else-if="type === 'unaudit'" class="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <svg v-else class="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <div class="flex-1">
            <h3 class="text-lg font-medium text-gray-900 mb-2">{{ computedTitle }}</h3>
            <p class="text-gray-600 mb-4">
              {{ computedMessage }}
              <span v-if="itemName" class="font-semibold text-gray-900">{{ itemName }}</span>
            </p>
            <div :class="computedWarningClass">
              <p class="text-sm" :class="computedWarningTextClass">
                <strong>注意：</strong>{{ computedWarningText }}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 对话框底部 -->
      <div class="flex justify-end space-x-3 px-6 pb-6">
        <button 
          class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" 
          @click="handleCancel"
          :disabled="loading"
        >
          取消
        </button>
        <button 
          :class="computedButtonClass"
          @click="handleConfirm"
          :disabled="loading"
        >
          <span v-if="loading" class="inline-flex items-center">
            <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ computedLoadingText }}
          </span>
          <span v-else>{{ computedConfirmText }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  type: {
    type: String,
    default: 'confirm',
    validator: (value) => ['delete', 'audit', 'unaudit', 'confirm'].includes(value)
  },
  title: {
    type: String,
    default: ''
  },
  message: {
    type: String,
    default: ''
  },
  itemName: {
    type: String,
    default: ''
  },
  warningText: {
    type: String,
    default: ''
  },
  loading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:visible', 'confirm', 'cancel'])

// 计算属性：根据类型返回默认值
const computedTitle = computed(() => {
  if (props.title) return props.title
  switch (props.type) {
    case 'delete': return '确认删除'
    case 'audit': return '确认审核'
    case 'unaudit': return '确认反审核'
    default: return '确认操作'
  }
})

const computedMessage = computed(() => {
  if (props.message) return props.message
  switch (props.type) {
    case 'delete': return '您确定要删除这条记录吗？'
    case 'audit': return '您确定要审核这条记录吗？'
    case 'unaudit': return '您确定要反审核这条记录吗？'
    default: return '您确定要执行此操作吗？'
  }
})

const computedWarningText = computed(() => {
  if (props.warningText) return props.warningText
  switch (props.type) {
    case 'delete': return '删除后将无法恢复，请确认操作。'
    case 'audit': return '审核后将锁定记录，无法修改和删除。'
    case 'unaudit': return '反审核后记录将变为可编辑状态，请谨慎操作。'
    default: return '请确认您的操作。'
  }
})

const computedWarningClass = computed(() => {
  const baseClass = 'border rounded-md p-3'
  switch (props.type) {
    case 'delete': return `${baseClass} bg-red-50 border-red-200`
    case 'audit': return `${baseClass} bg-blue-50 border-blue-200`
    case 'unaudit': return `${baseClass} bg-orange-50 border-orange-200`
    default: return `${baseClass} bg-yellow-50 border-yellow-200`
  }
})

const computedWarningTextClass = computed(() => {
  switch (props.type) {
    case 'delete': return 'text-red-800'
    case 'audit': return 'text-blue-800'
    case 'unaudit': return 'text-orange-800'
    default: return 'text-yellow-800'
  }
})

const computedButtonClass = computed(() => {
  const baseClass = 'px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'
  switch (props.type) {
    case 'delete': return `${baseClass} bg-red-600 hover:bg-red-700 focus:ring-red-500`
    case 'audit': return `${baseClass} bg-blue-600 hover:bg-blue-700 focus:ring-blue-500`
    case 'unaudit': return `${baseClass} bg-orange-600 hover:bg-orange-700 focus:ring-orange-500`
    default: return `${baseClass} bg-gray-600 hover:bg-gray-700 focus:ring-gray-500`
  }
})

const computedConfirmText = computed(() => {
  switch (props.type) {
    case 'delete': return '删除'
    case 'audit': return '确认审核'
    case 'unaudit': return '确认反审核'
    default: return '确认'
  }
})

const computedLoadingText = computed(() => {
  switch (props.type) {
    case 'delete': return '删除中...'
    case 'audit': return '审核中...'
    case 'unaudit': return '反审核中...'
    default: return '处理中...'
  }
})

const handleConfirm = () => {
  emit('confirm')
}

const handleCancel = () => {
  emit('cancel')
  emit('update:visible', false)
}
</script>

<style scoped>
/* 确保对话框在最顶层 */
.z-\[9999\] {
  z-index: 9999;
}
</style>